/**
 * Import Lib & assets
 */

import React, { useContext } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import t from "theme";

/**
 * import context
 */
import { AppContext } from "context/appContext";
import { ContentContext } from "context/contentContext";

/**
 * import components
 */

import { Item__rating } from "components/item/item";
import { Content__product, Content__error } from "components/content/content";
import { Svg__cross } from "components/svg/svg";

/**
 * styled components
 */

const Container = styled.div`
  min-height: 436px;
  width: 200px;
  margin: 0 20px 44px 20px;

  ${t.mixins.sm(`
    width: 172px;
    margin: 0 12px 23px 12px;
  `)}
`;
const Description = styled.div`
  padding-left: 13px;

  ${t.mixins.sm(`
    padding-left: 0;
  `)}
`;
const Button = styled.div`
  ${t.mixins.flex({ justify: "flex-start" })}
  padding: 12px 15px 12px 0;
  ${t.mixins.rem(13, 15)}
  font-family: ${t.font.primary};
  font-weight: ${t.font.bold};
  text-transform: uppercase;
  color: ${t.color.white};
  background-color: ${t.color.tertiary};
  border-radius: 2px;
  cursor: pointer;

  ${({ selected }) =>
    selected &&
    `
    background-color: ${t.color.white}
    border: solid 1px ${t.color.tertiary};
    color: ${t.color.tertiary};
  `}

  ${t.mixins.sm(`
    ${t.mixins.rem(13, 13)}
  `)}
`;
const Plus = styled.img.attrs({ src: t.image.plus })`
  margin: 0 10px;
  width: 25px;
  height: 25px;
`;
const Notch = styled.img.attrs({ src: t.image.notch })`
  margin: 0 10px;
  width: 25px;
  height: 25px;
`;
const Specs = styled.ul`
  min-height: 90px;
  padding-left: 18px;
  margin-bottom: 23px;
  list-style: square;
`;
const Spec = styled.li`
  ${t.mixins.rem(13, 18)}
  font-family: ${t.font.primary};
`;
const NoBulletSpec = styled.li`
  ${t.mixins.rem(13, 18)}
  font-family: ${t.font.primary};
  list-style: none;
`;
const Error = styled(Content__error)`
  max-width: 200px;
  transform: translateY(-20px);
`;

/**
 * product component is..
 */

const Item__product = ({ className, product, updateValue, selected, error }) => {
  const { translations } = useContext(AppContext);
  const { c, lang } = useContext(ContentContext);
  const { id, novelty, img, range, skus, name, price } = product;
  const list = ["volume", "warranty", "price"];
  const blacklistPrice = ["it"];
  const blacklisted = blacklistPrice.includes(lang);
  return (
    <Container className={className}>
      <Content__product novelty={novelty} img={img} range={range} skus={skus} />
      <Description>
        <Item__rating id={id} name={name} />
        <Specs>
          {product.specs.aspiration && <Spec>{translations["aspiration"]}</Spec>}
          {list.map((spec, index) => {
            if (spec !== "price") {
              return (
                <Spec key={index}>
                  {translations[spec]} : {product[spec]}
                </Spec>
              );
            }
            if (product[spec] && !blacklisted) {
              return (
                <>
                  <Spec key={index}>{translations[spec]} :</Spec>
                  <NoBulletSpec key={index}>{product[spec]}</NoBulletSpec>
                </>
              );
            }
          })}
        </Specs>
      </Description>
      {selected ? (
        <Button selected onClick={() => updateValue(id, false)}>
          <Notch />
          {c("home.products.selected")}
          <Svg__cross color={t.color.tertiary} size={18} style={{ marginLeft: "auto" }} />
        </Button>
      ) : (
        <Button onClick={() => updateValue(id, true)}>
          <Plus />
          {c("home.products.select")}
        </Button>
      )}
      {error && <Error content={c("home.products.error")} />}
    </Container>
  );
};

export default Item__product;

Item__product.propTypes = {
  /**
      A custom style object
      */
  style: PropTypes.object,
};

Item__product.defaultProps = {
  style: {},
};

/*
    import Item__product from 'components/item/product/item__product'
    <Item__product />
  */
